import { useQuery, UseQueryResult } from 'react-query';
import { IExposureHistory } from '../utils/exposureUtils';
import { getExposureHistory as getExposureHistoryApi, ExposureSnapshot } from '../services/exposureService';
import { GENERAL_ID } from '../components/Stores/exposureDataStore';

interface UseExposureHistoryOptions {
  accountId: string;
  materialId?: string;
  fromDate: Date;
  enabled?: boolean;
}

/**
 * Map API ExposureSnapshot → IExposureHistory shape used by chart components.
 */
function mapSnapshot(s: ExposureSnapshot): IExposureHistory {
  // For GENERAL (overall) snapshots, quantities are zeroed because they're
  // not meaningful across different commodities. Use base currency values
  // instead — the chart multiplies by price=1 for general, so this works.
  const isGeneral = s.formulaId === null;
  return {
    key: `${s.accountId}__${s.formulaCode || 'GENERAL'}__${s.snapshotDate}`,
    accountId: String(s.accountId),
    formulaId: s.formulaCode || 'GENERAL',
    createDate: new Date(s.snapshotDate),
    futureOrders: isGeneral ? s.demandValueBase : s.demandQuantityMt,
    inventory: isGeneral ? (s.onHandValueBase + s.poValueBase) : (s.onHandQuantityMt + s.poQuantityMt),
    financialPosition: isGeneral ? s.hedgeValueBase : s.hedgeQuantityMt,
  };
}

export const useExposureHistory = ({
  accountId,
  materialId,
  fromDate,
  enabled = true,
}: UseExposureHistoryOptions): UseQueryResult<IExposureHistory[], Error> => {
  const formulaCode = !materialId || materialId === GENERAL_ID ? 'GENERAL' : materialId;

  const queryKey = ['exposureHistory', accountId, formulaCode, fromDate.toISOString()];

  const queryFn = async (): Promise<IExposureHistory[]> => {
    const numericAccountId = parseInt(accountId);
    if (!numericAccountId) return [];

    const snapshots = await getExposureHistoryApi({
      accountId: numericAccountId,
      formulaCode,
      from: fromDate.toISOString().split('T')[0],
    });

    return snapshots.map(mapSnapshot);
  };

  return useQuery(queryKey, queryFn, {
    enabled: enabled && !!accountId,
    staleTime: 3 * 60 * 60 * 1000, // 3 hours
    cacheTime: 4 * 60 * 60 * 1000, // 4 hours
    refetchOnWindowFocus: false,
    refetchOnMount: 'always',
    retry: 2,
    retryDelay: (attemptIndex: number) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
};

// Convenience hook for fetching exposure history for a specific material
export const useExposureHistoryByMaterial = (
  accountId: string,
  formulaId: string,
  fromDate: Date,
  enabled?: boolean,
) => {
  return useExposureHistory({ accountId, materialId: formulaId, fromDate, enabled });
};

// Convenience hook for fetching all exposure history for an account
export const useExposureHistoryByAccount = (accountId: string, fromDate: Date, enabled?: boolean) => {
  return useExposureHistory({ accountId, fromDate, enabled });
};
